import { Link } from "@tanstack/react-router";

import {
  type EventLogRow,
  type EventLogSearch,
  eventPropertyEntries,
  formatEventDelay,
} from "../lib/analyticsEventLog";
import { formatPreciseDateTime } from "../lib/format";
import { DetailField, DetailGroup } from "./DetailFields";

/**
 * Every recorded field of one event, under the row it belongs to: grouped by what the field is
 * about, the properties decoded one per line, and on each id the copy and the filter it offers.
 * Two columns of two groups, paired so their heights come out close: a browser event is long on
 * who and short on properties, a server event the reverse.
 */
export function AnalyticsEventDetail({
  tenantId,
  row,
  onFilter,
}: {
  tenantId: string;
  row: EventLogRow;
  onFilter: (patch: Partial<EventLogSearch>) => void;
}) {
  const entries = eventPropertyEntries(row.properties);
  const delay = formatEventDelay(row.occurredAt, row.receivedAt);
  const { endUserId, anonymousId, sessionId } = row;
  return (
    // Its own container: the columns follow the width the panel is given, not the window's.
    <div className="@container">
      <div className="grid gap-x-12 gap-y-6 py-3 @3xl:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-6">
          <DetailGroup title="Event">
            <DetailField label="Name" mono>
              {row.eventName}
            </DetailField>
            <DetailField label="Source">{row.source}</DetailField>
            <DetailField label="Event id" mono copy={row.id}>
              {row.id}
            </DetailField>
            <DetailField label="Occurred">
              <time dateTime={row.occurredAt} title={row.occurredAt}>
                {formatPreciseDateTime(row.occurredAt)}
              </time>
            </DetailField>
            <DetailField label="Received">
              <time dateTime={row.receivedAt} title={row.receivedAt}>
                {formatPreciseDateTime(row.receivedAt)}
              </time>
              {delay && <span className="ml-2 text-muted-foreground tabular-nums">{delay}</span>}
            </DetailField>
            <DetailField label="Partition date" mono>
              {row.dt}
            </DetailField>
          </DetailGroup>

          <DetailGroup title="Context">
            <DetailField label="Route" mono>
              {row.route}
            </DetailField>
            {/* The dimensions the event was stamped with; empty reads "Unknown" in a breakdown. */}
            <DetailField label="Operating system" mono>
              {row.os}
            </DetailField>
            <DetailField label="Device" mono>
              {row.device}
            </DetailField>
            <DetailField label="Country" mono>
              {row.country}
            </DetailField>
            <DetailField label="Customer segment" mono>
              {row.segment}
            </DetailField>
            <DetailField label="Environment" mono copy={row.environmentId}>
              {row.environmentId}
            </DetailField>
            <DetailField label="Tenant" mono copy={row.tenantId}>
              {row.tenantId}
            </DetailField>
          </DetailGroup>
        </div>
        <div className="flex min-w-0 flex-col gap-6">
          <DetailGroup title="Properties">
            {entries === null ? (
              // Stored text that is not a JSON object: shown as it is rather than guessed at.
              <pre className="max-h-48 overflow-auto font-mono text-xs leading-5 whitespace-pre-wrap">
                {row.properties}
              </pre>
            ) : entries.length === 0 ? (
              <p className="py-0.5 text-sm leading-5 text-muted-foreground">
                This event carries no properties.
              </p>
            ) : (
              entries.map((entry) => (
                <DetailField
                  key={entry.key}
                  label={entry.key}
                  labelMono
                  mono
                  copy={entry.isText ? entry.value : null}
                >
                  {entry.key === "boxId" && entry.isText ? (
                    <Link
                      to="/tenants/$tenantId/boxes/$boxId"
                      params={{ tenantId, boxId: entry.value }}
                      className="text-primary hover:underline"
                    >
                      {entry.value}
                    </Link>
                  ) : (
                    entry.value
                  )}
                </DetailField>
              ))
            )}
          </DetailGroup>

          <DetailGroup title="Who">
            <DetailField
              label="Shopper id"
              mono
              copy={endUserId}
              filter={
                endUserId
                  ? { label: "Only this shopper", onSelect: () => onFilter({ user: endUserId }) }
                  : null
              }
            >
              {endUserId && (
                <Link
                  to="/tenants/$tenantId/users/$userId"
                  params={{ tenantId, userId: endUserId }}
                  className="text-primary hover:underline"
                >
                  {endUserId}
                </Link>
              )}
            </DetailField>
            <DetailField label="Username">{row.endUser?.username}</DetailField>
            <DetailField label="External id" mono copy={row.endUser?.externalId}>
              {row.endUser?.externalId}
            </DetailField>
            <DetailField
              label="Browser id"
              mono
              copy={anonymousId}
              filter={
                anonymousId
                  ? { label: "Only this browser", onSelect: () => onFilter({ anon: anonymousId }) }
                  : null
              }
            >
              {anonymousId}
            </DetailField>
            <DetailField
              label="Tab id"
              mono
              copy={sessionId}
              filter={
                sessionId
                  ? { label: "Only this tab", onSelect: () => onFilter({ session: sessionId }) }
                  : null
              }
            >
              {sessionId}
            </DetailField>
            <DetailField label="User agent">
              {row.userAgent && <span className="text-xs">{row.userAgent}</span>}
            </DetailField>
          </DetailGroup>
        </div>
      </div>
    </div>
  );
}
